import { AsyncPipe, formatCurrency, formatPercent } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  OnInit,
  output,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  AccountSelectionService,
  adStatsGroupBy,
  arrayGroupBy,
  arrayGroupBy2D,
  AuthService,
  BrandAssetsService,
  CurrencyService,
  fblMode,
  FblModeSize,
  groupBy,
  SegmentService,
  SpStrategyService,
  StatsService,
  StrategyCache,
  TacosStrategiesService,
  UserSelectionService,
} from "@front/m19-services";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  ColGroupDef,
  CsvExportParams,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  ProcessCellForExportParams,
  ShouldRowBeSkippedParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { emptyColumnHeader, exportGridCsv } from "@m19-board/grid-config/grid-config";
import { StrategyStats } from "@m19-board/models/Metric";

import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  AccountMarketplace,
  AccountState,
  AlgoMode,
  CampaignType,
  Currency,
  SbCreative,
  StatsCampaignType,
  StrategyConfigHistory,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { gridConfig, isFooterRow, resetState } from "@front/m19-grid-config";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  ROAS,
  TACOS,
} from "@front/m19-metrics";
import {
  AdStatsEx,
  AlgoModeStr,
  CurrencySymbols,
  Marketplaces,
  SegmentEx,
  StrategyEx,
  StrategyGroupEx,
} from "@front/m19-models";
import {
  ActionButton,
  ActionButtonsComponent,
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupPrefixDirective,
  IMetricEvoGridComponent,
  ModalService,
} from "@front/m19-ui";
import { getYesterdayDate, mergeSeveralDates, sumAdStats } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { AlgoTargetGridRendererComponent } from "@m19-board/insights/advertising-stats/algo-target-grid-renderer/algo-target-grid-renderer.component";
import { CampaignTypeBadgeGridComponent } from "@m19-board/shared/campaign-type-badge/campaign-type-badge-grid.component";
import { LinkComponent } from "@m19-board/shared/link/link.component";
import { StrategyLinkGridComponent } from "@m19-board/shared/strategy-link-grid/strategy-link-grid.component";
import { FblModeComponent } from "@m19-board/strategies/fbl-mode/fbl-mode.component";
import {
  StrategyStatsComponent,
  StrategyStatsInputData,
} from "@m19-board/strategies/strategies/strategy-stats/stategy-stats.component";
import {
  combineLatest,
  combineLatestWith,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from "rxjs";
import { actionColumnProperties, CurrencyColumn } from "../../grid-config/grid-columns";

export interface GlobalStrategyStats extends StrategyStats {
  algoMode: string;
}

@UntilDestroy()
@Component({
  selector: "app-advertising-stats-grid",
  template: ` <div class="ag-theme-quartz w-full" *transloco="let t">
    <div class="mb-3 flex justify-between">
      <div iInputGroup class="w-96">
        <IIcon name="search" iInputGroupPrefix />
        <input
          iInputGroupInput
          [(ngModel)]="strategyNameFilter"
          size="sm"
          [placeholder]="t('overview.search_by_strategy_name_or_group_name')"
        />
      </div>
      <div class="flex justify-end gap-2">
        <button iButton variant="secondary" size="sm" (click)="resetCols()">
          {{ t("common.restore_columns") }}
        </button>
        <button iButton variant="secondary" size="sm" (click)="export.emit()">
          <IIcon name="upload" />
          {{ t("common.export") }}
        </button>
      </div>
    </div>
    <ag-grid-angular [rowData]="(gridData$ | async) ?? []" [gridOptions]="gridOptions" class="h-[60vh] w-full" />
  </div>`,
  imports: [
    AgGridAngular,
    AsyncPipe,
    IButtonDirective,
    TranslocoDirective,
    FormsModule,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupPrefixDirective,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvertisingStatsGridComponent implements OnInit {
  private authService = inject(AuthService);
  private statsService = inject(StatsService);
  private tacosStrategyService = inject(TacosStrategiesService);
  private segmentService = inject(SegmentService);
  private userSelectionService = inject(UserSelectionService);
  private accountSelectionService = inject(AccountSelectionService);
  private translocoService = inject(TranslocoService);
  private spStrategyService = inject(SpStrategyService);
  private modalService = inject(ModalService);

  private readonly strategyCache = inject(StrategyCache);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly currencyService = inject(CurrencyService);
  private readonly brandAssetService = inject(BrandAssetsService);

  readonly METRIC_COLUMNS: Metric<StrategyStats>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];

  @Input()
  gridKey: string = "overviewGrid2";
  @Input()
  hiddenColumns: string[] = [];
  dataFilter = input<(globalStrategyStats: GlobalStrategyStats) => boolean>(() => true);
  @Input()
  noStrategyLinks = false;
  @Input()
  fullHeight = false;
  export = output<void>();

  locale = "fr";
  currency = Currency.EUR;
  isReadOnly = false;
  accountMarketplace?: AccountMarketplace;

  strategyIndex: Map<number, StrategyEx> = new Map();
  segmentIndex: Map<number, SegmentEx> = new Map();
  strategyGroupIndex: Map<number, StrategyGroupEx> = new Map();

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  gridData$?: Observable<StrategyStats[]>;
  private dailyCampaignStats$!: Observable<AdStatsEx[]>;
  private previousDailyCampaignStats$!: Observable<AdStatsEx[]>;
  private strategyTargetHistory$!: Observable<StrategyConfigHistory[]>;
  private dataFilter$ = toObservable(this.dataFilter);

  previousDataMap: Map<number | StatsCampaignType, AdStatsEx> = new Map<number | StatsCampaignType, AdStatsEx>(); // <strategyId, aggPreviousData>
  previousDataArrayMap: Map<number | StatsCampaignType, AdStatsEx[]> = new Map<
    number | StatsCampaignType,
    AdStatsEx[]
  >(); // <strategyId, previousData[]>

  shareOfBudgetUsedByStrategy = new Map<string, number>();
  totalBudgetCostRatio = 0;
  totalBudgetOfYesterday = 0;

  tacosStatsBySpId: Map<number, AdStatsEx> = new Map<number, AdStatsEx>();
  previousTacosStatsBySpId: Map<number, AdStatsEx> = new Map<number, AdStatsEx>();
  strategyNameFilter = signal<string>("");

  // Map used on parent row to get strategy info
  strategyNameOrSub: Map<number, string> = new Map();

  creativesPerStrategy: Map<number, SbCreative[]> = new Map();

  isExternalFilterPresent = (): boolean => {
    return this.strategyNameFilter() !== "";
  };

  doesExternalFilterPass = (node: IRowNode<GlobalStrategyStats>): boolean => {
    const strategyName = node.data?.strategyName?.toLowerCase();
    const filter = this.strategyNameFilter().toLowerCase();
    if (strategyName?.includes(filter)) return true;

    const strategyGroup = this.strategyGroupIndex.get(node.data?.strategy?.strategyGroupId ?? -1);
    if (strategyGroup?.strategyGroupName?.toLowerCase().includes(filter)) return true;

    return false;
  };

  defaultColDef: ColDef = {
    filter: true,
    useValueFormatterForExport: true,
  };

  colDefs: (ColDef<GlobalStrategyStats> | ColGroupDef<GlobalStrategyStats>)[] = [
    {
      field: "state",
      headerName: "State",
      pinned: "left",
      hide: true,
      suppressColumnsToolPanel: true,
      width: 50,
      filterValueGetter: (params) => {
        if (!params.data?.state) return "";
        return params.data.state == StrategyStateEnum.ENABLED
          ? this.translocoService.translate("common.active_strategy")
          : this.translocoService.translate("common.paused_strategy");
      },
      cellClass: "flex items-center",
      cellRenderer: (params: ICellRendererParams<GlobalStrategyStats>) => {
        const color = params.data?.state == StrategyStateEnum.ENABLED ? "bg-green-500" : "bg-gray-300";
        return `<div class="h-2 w-2 rounded-full ${color}"></div>`;
      },
    },
    {
      field: "strategyName",
      headerName: "Strategy",
      pinned: "left",
      cellClass: "!flex !items-center",
      cellClassRules: {
        "!text-gray-500": (params) =>
          !params.node.data?.strategyId || !this.strategyIndex.get(params.node.data.strategyId),
      },
      width: 240,
      cellRendererSelector: (params) => {
        if (!params.data?.strategyId || !this.strategyIndex.get(params.data.strategyId)) return undefined;
        return {
          component: StrategyLinkGridComponent,
          params: { strategyId: params.data.strategyId },
        };
      },
    },
    {
      field: "campaignType",
      ...emptyColumnHeader(),
      headerName: "Campaign Type",
      pinned: "left",
      width: 60,
      valueFormatter: (params: ValueFormatterParams<GlobalStrategyStats, CampaignType>) => {
        return params.value && !params.node?.isRowPinned() ? params.value : "";
      },
      cellRendererSelector: (params: ICellRendererParams<GlobalStrategyStats>) => {
        if (!params.value || params.node.isRowPinned()) return undefined;
        return {
          component: CampaignTypeBadgeGridComponent,
          params: {
            campaignType: params.value,
            tacosSpStrategyId: params.data?.tacosTarget ? params.data?.strategyId : undefined,
          },
        };
      },
    },
    {
      headerName: this.translocoService.translate("strategy-table.target", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("strategy-table.target"),
      filter: false,
      sortable: false,
      colId: "targetAcos",
      cellRendererSelector: (params: ICellRendererParams<GlobalStrategyStats>) => {
        return {
          component: AlgoTargetGridRendererComponent,
          params: {
            strategy: params.data?.strategy,
            locale: this.locale,
            currency:
              params.data?.strategy?.campaignType === CampaignType.DSP
                ? this.accountMarketplace?.dspAdvertiser?.currency
                : this.accountMarketplace?.marketplace
                  ? Marketplaces[this.accountMarketplace.marketplace].currency
                  : Currency.USD,
            readonly: this.isReadOnly,
            organizationId: this.accountMarketplace?.resourceOrganizationId,
            addLabel: true,
          },
        };
      },
    },
    {
      headerName: this.translocoService.translate("overview-grid.strategy_group", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("overview-grid.strategy_group"),
      colId: "strategyGroup",
      pinned: "left",
      filter: "agTextColumnFilter",
      width: 150,
      hide: true,
      valueGetter: (params: ValueGetterParams<GlobalStrategyStats>) => {
        if (params.data?.strategy?.strategyGroupId) {
          return this.strategyGroupIndex.get(params.data.strategy.strategyGroupId)?.strategyGroupName;
        }
        return undefined;
      },
      cellRendererSelector: (params) => {
        if (!params.value) {
          return undefined;
        }
        return {
          component: LinkComponent,
          params: {
            routerLink: "/advertising/sponsored-product/strategy-group/" + params.data?.strategy?.strategyGroupId,
            target: "_blank",
            content: params.value,
            queryParamsHandling: "merge",
          },
        };
      },
    },
    {
      headerName: this.translocoService.translate("overview-grid.mode"),
      headerValueGetter: () => this.translocoService.translate("overview-grid.mode"),
      hide: true,
      width: 120,
      colId: "algoMode",
      sortable: false,
      filterValueGetter: (params) => {
        if (!params.data?.strategyId) return "";

        const strategy = this.strategyIndex.get(params.data.strategyId);

        return strategy?.constraint && fblMode[strategy?.constraint]?.title
          ? this.translocoService.translate(fblMode[strategy?.constraint].title)
          : "";
      },
      cellRendererSelector: (params) => {
        if (!params.data?.strategyId) return undefined;

        const strat = this.strategyIndex.get(params.data.strategyId);
        if (strat) return { component: FblModeComponent, params: { strategy: strat, size: FblModeSize.small } };
        return undefined;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: this.translocoService.translate("sd-strategy-creation.algorithm", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sd-strategy-creation.algorithm"),
      hide: true,
      field: "algorithm",
      tooltipValueGetter: (params: ITooltipParams<GlobalStrategyStats, AlgoMode>) =>
        AlgoModeStr[params.value!]?.description,
      valueFormatter: (params: ValueFormatterParams<GlobalStrategyStats, AlgoMode>) =>
        this.translocoService.translate(AlgoModeStr[params.value!]?.shortDescription),
      filterValueGetter: (params: ValueGetterParams<GlobalStrategyStats>) =>
        params.data?.algorithm
          ? this.translocoService.translate(AlgoModeStr[params.data.algorithm! as AlgoMode]?.shortDescription)
          : "",
    },
    {
      headerName: this.translocoService.translate("advertising.budget_usage"),
      filter: false,
      colId: "budget",
      hide: true,
      valueGetter: (params: ValueGetterParams<GlobalStrategyStats>) => {
        if (isFooterRow(params)) {
          return this.totalBudgetCostRatio;
        }

        if (!params.data?.strategyId) return null;
        return this.shareOfBudgetUsedByStrategy.get(params.data.strategyId.toString());
      },
      valueFormatter: (params: ValueFormatterParams<GlobalStrategyStats, number>) => {
        if (!params.value) return "-";
        return formatPercent(Math.min(params.value, 1), this.locale, "1.0-0");
      },
    },
    {
      headerName: this.translocoService.translate("switch-target-algo-modal.suggested_bid", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("switch-target-algo-modal.suggested_bid"),
      colId: "suggestedBid",
      field: "suggestedBid",
      hide: true,
      valueFormatter: (params: ValueFormatterParams<GlobalStrategyStats, number>) => {
        return params.value
          ? formatCurrency(params.value, this.locale, CurrencySymbols[this.currency], this.currency, "1.0-0")
          : "-";
      },
    },
    {
      headerName: this.translocoService.translate("metrics.TACOS_titleSmall"),
      headerTooltip: this.translocoService.translate("metrics.TACOS_tooltip"),
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      colId: "tacos",
      enableValue: true,
      enablePivot: true,
      hide: true,
      valueGetter: (params: ValueGetterParams<GlobalStrategyStats>) => {
        if (!params.data?.strategyId) return null;
        const adStats = this.tacosStatsBySpId.get(params.data.strategyId);
        if (!adStats) return null;
        const val = TACOS.value(adStats);
        return val === undefined || isNaN(val) || !isFinite(val) ? null : val;
      },
      cellRenderer: IMetricEvoGridComponent,
      cellRendererParams: (params: ICellRendererParams) => {
        const currentData = this.tacosStatsBySpId.get(params.data?.strategyId) ?? {};
        return {
          metric: TACOS,
          locale: this.locale,
          currentData: currentData ?? {},
          currency: this.currency,
          displayValue: true,
          previousData: this.getPreviousDataNode(params.node) ?? {},
        };
      },
    },
    ...this.gridConfigService
      .getMetricsColDef<GlobalStrategyStats>(this.METRIC_COLUMNS)
      .map(({ headerName, headerTooltip, ...def }: ColDef<GlobalStrategyStats>) => ({
        ...def,
        headerName,
        headerTooltip,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            ...(def as ColDef<GlobalStrategyStats>).cellRendererParams(params),
            previousData: this.getPreviousDataNode(params.node),
            currency: this.currency,
            locale: this.locale,
          };
        },
      })),
    CurrencyColumn,
    {
      ...actionColumnProperties<GlobalStrategyStats, string>(),
      cellRendererSelector: (params) => {
        const isTotalRow = params.node.isRowPinned();

        const btns: ActionButton[] = [
          {
            icon: "chartLine",
            tooltip: isTotalRow
              ? this.translocoService.translate("overview-grid.display_total_strategy_graph")
              : this.translocoService.translate("overview-grid.display_strategy_graph"),
            onClick: (params: ICellRendererParams) => {
              this.displayStrategyGraph(params, isTotalRow);
            },
          },
        ];

        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons: btns,
          },
        };
      },
    },
  ];

  gridOptions: GridOptions = {
    defaultColDef: this.defaultColDef,
    columnDefs: this.colDefs.filter((c: ColDef) => !this.hiddenColumns.includes(c.colId!)),
    doesExternalFilterPass: this.doesExternalFilterPass,
    isExternalFilterPresent: this.isExternalFilterPresent,
    ...gridConfig({
      gridKey: this.gridKey,
      withFooterRow: true,
    }),
  };

  // TODO(hugo|joseph|clément): this observable can probably be merged with allStats$ above
  private fillTacosStats$ = combineLatest([
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.tacosStrategyService.getTacosStrategyIndex(am.accountId!, am.marketplace!)),
    ),
    this.userSelectionService.periodComparison$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    switchMap(([am, dr, tacosStrategyIndex, pc, currency]) => {
      return tacosStrategyIndex.size > 0
        ? forkJoin(
            Array.from(tacosStrategyIndex.values()).map((ts) =>
              this.tacosStrategyService
                .getStats(am, dr[0], dr[1], currency, pc?.period, ts.tacosStrategyGroupId!)
                .pipe(take(1)),
            ),
          )
        : of([]);
    }),
    tap((stats) => {
      const statsByStrategyId: Map<number, AdStatsEx> = new Map();
      const previousStatsByStrategyId: Map<number, AdStatsEx> = new Map();

      for (const s of stats) {
        const agg = s.data.reduce((acc, curr) => {
          return sumAdStats(acc, curr);
        }, {} as AdStatsEx);
        if (s.data.length > 0) {
          statsByStrategyId.set(s.data[0].strategyId!, agg);
        }

        const previousAgg = s.previousPeriodData.reduce((acc, curr) => {
          return sumAdStats(acc, curr);
        }, {} as AdStatsEx);
        if (s.previousPeriodData.length > 0) {
          previousStatsByStrategyId.set(s.previousPeriodData[0].strategyId!, previousAgg);
        }
      }
      this.tacosStatsBySpId = statsByStrategyId;
      this.previousTacosStatsBySpId = previousStatsByStrategyId;
      this.agGrid?.api.redrawRows();
    }),
  );

  constructor() {
    localStorage.removeItem("overviewGrid");
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => this.brandAssetService.getSbCreativesPerStrategy(am.accountId, am.marketplace)),
        untilDestroyed(this),
      )
      .subscribe((creatives) => {
        this.creativesPerStrategy = creatives;
      });

    toObservable(this.strategyNameFilter)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.agGrid?.api.onFilterChanged();
      });
  }

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace)),
        untilDestroyed(this),
      )
      .subscribe((strategyGroupIndex) => {
        this.strategyGroupIndex = strategyGroupIndex;
        this.agGrid?.api.refreshCells();
      });
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(untilDestroyed(this))
      .subscribe((am: AccountMarketplace) => {
        this.accountMarketplace = am;
      });
    combineLatest([
      this.accountSelectionService.readOnlyMode$,
      this.accountSelectionService.singleAccountMarketplaceSelection$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isReadOnly, am]) => {
        this.isReadOnly = isReadOnly || am.state !== AccountState.BIDDER_ON;
      });

    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.locale = user.locale;
    });
    this.userSelectionService.selectedCurrency$.pipe(untilDestroyed(this)).subscribe((currency) => {
      this.currency = currency;
      this.agGrid?.api?.redrawRows();
    });

    this.previousDailyCampaignStats$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, pc, currency]) => {
        if (!pc?.period) {
          return of([]);
        }
        return this.statsService.getDailyCampaignStats(
          am.accountId,
          am.marketplace,
          currency,
          pc.period[0],
          pc.period[1],
        );
      }),
      shareReplay(1),
    );

    this.previousDailyCampaignStats$.pipe(untilDestroyed(this)).subscribe((previousData) => {
      this.previousDataMap.clear();
      this.previousDataArrayMap.clear();

      // Group by strategyId or campaign type if strategy not operated by m19
      this.previousDataArrayMap = arrayGroupBy(previousData, (x) => x.strategyId ?? x.campaignType!);
      this.previousDataMap = groupBy(previousData, (x) => x.strategyId ?? x.campaignType!, mergeSeveralDates);

      // Have to redraw rows because we need all the row data and not just the cells
      this.agGrid?.api.redrawRows();
    });

    this.dailyCampaignStats$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        combineLatest([
          this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
          am.dspAdvertiserId
            ? this.statsService.getDailyDspCampaignStats(
                am.accountId,
                am.marketplace,
                am.dspAdvertiserId!,
                currency,
                dr[0],
                dr[1],
              )
            : of([]),
        ]),
      ),
      map(([data, dspData]) => {
        return [...data, ...dspData];
      }),
      shareReplay(1),
    );

    this.strategyTargetHistory$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        this.statsService.getStrategyConfigHistory(
          am.accountId,
          am.marketplace,
          currency,
          am.dspAdvertiserId,
          dr[0],
          dr[1],
        ),
      ),
      shareReplay(1),
    );

    this.gridData$ = this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) =>
          combineLatest([
            this.tacosStrategyService.getTacosStrategyIndex(am.accountId, am.marketplace),
            this.dailyCampaignStats$!,
            this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace),
            this.segmentService.getSegments(am.accountId, am.marketplace),
            this.dataFilter$,
            this.fillTacosStats$,
          ]),
        ),
      )
      .pipe(
        untilDestroyed(this),
        map(([tacosStrategyIndex, data, strategyIndex, segmentIndex, dataFilter, _]) => {
          this.segmentIndex = segmentIndex;
          this.strategyIndex = new Map(Array.from(strategyIndex.entries()).map(([k, s]) => [k, s]));

          const tacosStrategies = Array.from(tacosStrategyIndex.values());

          const strategyStats = Array.from(
            adStatsGroupBy<string>(data, (x) => this.strategyKey(x.strategyId, x.campaignType!)).values(),
          );
          const globalStrategyStats: GlobalStrategyStats[] = strategyStats
            .filter((d) => {
              const strategy = this.strategyIndex.get(d.strategyId!);
              return !strategy?.isTacos() || strategy.campaignType === CampaignType.SP;
            })
            .map((d: AdStatsEx) => {
              const strategy = this.strategyIndex.get(d.strategyId!);

              let stats = d;
              // get related tacos strategy
              if (strategy?.isSpStrategy() && strategy?.algoMode === AlgoMode.TACOS_TARGET) {
                stats = this.tacosStatsBySpId.get(d.strategyId!) ?? d;
                strategy.tacosTarget = tacosStrategies.find(
                  (ts) => strategy.strategyId === ts.spStrategyId,
                )?.tacosTarget;
              }
              const strategyName = d.strategyId
                ? this.strategyIndex.has(d.strategyId)
                  ? this.strategyIndex.get(d.strategyId)!.name
                  : this.translocoService.translate("hourly-page.deleted_strategy")
                : this.translocoService.translate("hourly-page.not_operated_campaigns");
              const strategyStats: StrategyStats = toStrategyStats(stats, strategy, strategyName ?? "");

              if (strategy?.strategyId) this.strategyNameOrSub.set(strategy.strategyId, strategyStats?.strategyName);
              return {
                ...strategyStats,
                algoMode: strategy ? fblMode[strategy.constraint!]?.name : "",
              };
            })
            .filter((stats) => dataFilter(stats))
            .sort((a, b) => (b.adSales ?? 0) - (a.adSales ?? 0));

          return globalStrategyStats;
        }),
      );

    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap(() => this.dailyCampaignStats$),
        combineLatestWith(this.strategyTargetHistory$),
        map(([data, history]) => {
          const { shareOfBudgetUsedById, totalBudget, totalCost, totalBudgetOfYesterday } = this.computeBudgetUsage(
            history,
            data,
          );

          this.totalBudgetCostRatio = totalCost / totalBudget;
          this.totalBudgetOfYesterday = totalBudgetOfYesterday;

          return shareOfBudgetUsedById;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((shareOfBudgetUsedByStrategy) => {
        this.shareOfBudgetUsedByStrategy = shareOfBudgetUsedByStrategy;
      });
  }

  resetCols() {
    resetState(this.gridKey, this.agGrid.api);
  }

  exportGridCsv(fileName: string): void {
    const exportParams: CsvExportParams = {
      shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<StrategyStats>) => {
        const strategyName = params.node.data?.strategyName;
        if (!strategyName) {
          return true;
        }
        const strategy = params.node.data?.strategyId
          ? this.strategyIndex.get(params.node.data.strategyId!)
          : undefined;
        if (!strategy) {
          return true;
        }
        return false;
      },
      processCellCallback: (params: ProcessCellForExportParams<StrategyStats>) => {
        if (params.column.getId() === "algoMode") {
          const strategy = this.strategyIndex.get(params.node!.data!.strategyId!)!;
          return fblMode[strategy.constraint!]?.name;
        }
        return params.formatValue(params.value);
      },
      columnKeys: this.agGrid?.api.getColumns()?.filter((c) => {
        if (c.getId() == "targetAcos") {
          return false;
        }
        return c.getId() != "actions";
      }),
      fileName: fileName,
    };

    exportGridCsv(this.agGrid?.api, exportParams);
  }

  private computeBudgetUsage(history: StrategyConfigHistory[], data: AdStatsEx[]) {
    const strategyHistoryById = arrayGroupBy<string, StrategyConfigHistory>(
      history,
      (x) => x.strategyId?.toString() ?? "",
    );
    const strategyStatsById = arrayGroupBy2D(
      data,
      (x) => x.strategyId?.toString() ?? "",
      (x) => x.date?.toString() ?? "",
    );

    const shareOfBudgetUsedById = new Map<string, number>();

    let totalBudget = 0;
    let totalCost = 0;
    let totalBudgetOfYesterday = 0;

    for (const strat of strategyHistoryById.keys()) {
      const history = strategyHistoryById.get(strat)!;
      let budgetOnPeriod = 0;
      let costOnPeriod = 0;
      const yesterday = getYesterdayDate();

      for (const h of history) {
        if (h.computedDailyBudget) {
          const cost =
            strategyStatsById
              .get(strat)
              ?.get(h.date!)
              ?.reduce((acc, curr) => acc + (curr.cost ?? 0), 0) ?? 0;

          costOnPeriod += cost;
        }

        budgetOnPeriod += h.computedDailyBudget ? h.computedDailyBudget : 0;

        if (h.date === yesterday) {
          totalBudgetOfYesterday += h.computedDailyBudget ?? 0;
        }
      }

      shareOfBudgetUsedById.set(strat, costOnPeriod / budgetOnPeriod);

      totalBudget += budgetOnPeriod;
      totalCost += costOnPeriod;
    }

    return { shareOfBudgetUsedById, totalBudget, totalCost, totalBudgetOfYesterday };
  }

  private getPreviousDataNode(node: IRowNode<GlobalStrategyStats>): AdStatsEx | undefined {
    if (node.isRowPinned() || !node.data) {
      return undefined;
    }

    if (node.data.tacosTarget) {
      return this.previousTacosStatsBySpId.get(node.data.strategyId!);
    }

    return this.previousDataMap.get(node.data.strategyId ?? node.data.campaignType!);
  }

  private strategyKey(strategyId: number | undefined, campaignType: StatsCampaignType | undefined): string {
    return (strategyId ?? "") + (campaignType ?? "");
  }

  private displayStrategyGraph(params: ICellRendererParams<GlobalStrategyStats>, totalGraph = false) {
    let strategyIds: Set<number> | undefined;
    let strategy: StrategyEx | undefined;
    let notOperatedCampaigns: Set<StatsCampaignType> | undefined;
    let title = "";

    if (totalGraph) {
      strategyIds = this.getTotalGraphStrategyIds(params);
      notOperatedCampaigns = this.getTotalGraphNotOperatedCampaigns(params);
      title = this.translocoService.translate("common.total");
    } else {
      const strategyId = params.node?.data?.strategyId;
      if (strategyId !== undefined) {
        strategy = this.strategyIndex.get(strategyId);
        if (strategy == undefined) {
          strategyIds = new Set([strategyId]);
          title = this.translocoService.translate("hourly-page.deleted_strategy");
        } else {
          title = strategy.name;
        }
      } else if (params.node.data !== undefined) {
        notOperatedCampaigns = new Set([params.node.data.campaignType!]);
        title = this.translocoService.translate("hourly-page.not_operated_campaigns");
      }
    }

    this.modalService.openModal<StrategyStatsInputData, void>(StrategyStatsComponent, {
      modalTitle: title,
      data: {
        strategy: strategy,
        strategyIds: strategyIds,
        isReadOnly: this.isReadOnly,
        withStrategyConfigMetrics: !totalGraph,
        notOperatedCampaigns: notOperatedCampaigns,
        withTargetingsSection: strategy?.campaignType === CampaignType.SP,
        withTacticSection: !!(
          strategy &&
          strategy.strategyId &&
          strategy.strategyId > 0 &&
          strategy.campaignType !== CampaignType.DSP
        ),
      },
    });
  }

  private getTotalGraphStrategyIds(params: ICellRendererParams<GlobalStrategyStats>): Set<number> {
    const result = new Set<number>();
    params.api.forEachNodeAfterFilter((n: IRowNode<GlobalStrategyStats>) => {
      if (n.data?.strategyId) {
        result.add(n.data?.strategyId);
      }
    });
    return result;
  }

  private getTotalGraphNotOperatedCampaigns(params: ICellRendererParams<GlobalStrategyStats>): Set<StatsCampaignType> {
    const result = new Set<StatsCampaignType>();
    params.api.forEachNodeAfterFilter((n: IRowNode<GlobalStrategyStats>) => {
      if (n.data && n.data.strategyId === undefined) {
        result.add(n.data.campaignType!);
      }
    });
    return result;
  }
}

export function toStrategyStats(
  adStats: AdStatsEx,
  strategy: StrategyEx | undefined,
  strategyName: string | undefined,
): StrategyStats {
  const daypartingEnabled = strategy?.isSpStrategy()
    ? strategy?.daypartingPauseHour != null && strategy?.daypartingReactivationHour != null
    : false;

  const daypartingPauseHour = strategy?.isSpStrategy() ? strategy?.daypartingPauseHour : null;
  const daypartingReactivationHour = strategy?.isSpStrategy() ? strategy?.daypartingReactivationHour : null;

  return {
    ...adStats,
    strategyId: strategy?.strategyId ?? adStats.strategyId,
    subStrategyId: undefined,
    strategyName: strategyName ?? "",
    state: strategy?.state,
    campaignType: adStats.campaignType,
    dayPartingEnabled: daypartingEnabled,
    dayPartingPauseHour: daypartingPauseHour,
    dayPartingReactivationHour: daypartingReactivationHour,
    algorithm: strategy?.algoMode,
    acosTarget: strategy?.algoMode === AlgoMode.ACOS_TARGET ? strategy?.acosTarget : undefined,
    suggestedBid: strategy?.algoMode === AlgoMode.PRODUCT_LAUNCH ? strategy?.suggestedBid : undefined,
    dailyBudget: strategy?.algoMode === AlgoMode.PRODUCT_LAUNCH ? strategy?.dailyBudget : undefined,
    minDailySpend: strategy?.algoMode === AlgoMode.PRODUCT_LAUNCH ? strategy?.minDailySpend : undefined,
    monthlyBudget: strategy?.algoMode === AlgoMode.MONTHLY_BUDGET_TARGET ? strategy?.monthlyBudget : undefined,
    tacosTarget:
      strategy?.isSpStrategy() && strategy?.algoMode === AlgoMode.TACOS_TARGET ? strategy?.tacosTarget : undefined,
    today: strategy?.today,
    nextMonthlyBudget: strategy?.nextMonthlyBudget,
    strategy: strategy,
  };
}
